import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import {
  ADVIA_CHART_OPTS,
  ADVIA_CHART_TOKENS,
  ADVIA_ECHARTS_THEME,
  CHART_SERIES,
  solid,
  tooltipRow,
  tooltipTitle,
} from '@/lib/advia-charts';
import { dayMonth, eur, num, pct } from './format';
import type { ReportLine } from './types';

interface LineSpendChartProps {
  line: ReportLine;
}

const CHART_HEIGHT = 200;

/** Launch and pause days don't say anything about the cap: drawn a tone lower. */
const MUTED_BAR = ADVIA_CHART_TOKENS.neutralLight;

interface ChartSpec {
  /** € caps are compared with spend; impression caps with impressions served. */
  measure: 'cost' | 'units';
  capSeries: Array<number | null> | null;
  /** Impression caps only exist as today's value, drawn as a flat reference. */
  capReference: number | null;
}

function chartSpec(line: ReportLine): ChartSpec {
  const euroCaps = line.daily.map((day) => day.cap);
  if (euroCaps.some((cap) => cap !== null)) {
    return { measure: 'cost', capSeries: euroCaps, capReference: null };
  }
  if (line.cap_imp !== null) {
    return { measure: 'units', capSeries: null, capReference: line.cap_imp };
  }
  return { measure: 'cost', capSeries: null, capReference: null };
}

type Measure = ChartSpec['measure'];

/** Head room over the cap so its label isn't glued to the top of the chart. */
const CAP_HEADROOM = 1.1;

/**
 * A cap above every bar would fall off the axis ECharts picks, so stretch it
 * to a round number over the cap; otherwise leave the axis to ECharts.
 */
function axisMaxForCap(line: ReportLine, spec: ChartSpec): number | undefined {
  if (spec.capReference === null) return undefined;
  const dataMax = Math.max(0, ...line.daily.map((day) => (spec.measure === 'cost' ? day.cost : day.units) ?? 0));
  if (spec.capReference <= dataMax) return undefined;
  const target = spec.capReference * CAP_HEADROOM;
  const step = Math.pow(10, Math.floor(Math.log10(target))) / 2;
  return Math.ceil(target / step) * step;
}

function formatMeasure(measure: Measure, value: number | null): string {
  return measure === 'cost' ? eur(value) : num(value);
}

/** Daily spend (or impressions, for impression caps) against the line's daily cap. */
export function LineSpendChart({ line }: LineSpendChartProps) {
  const spec = useMemo(() => chartSpec(line), [line]);

  const option = useMemo<EChartsOption>(() => {
    const barColor = solid(CHART_SERIES.graphite);
    const capColor = solid(CHART_SERIES.amber);

    return {
      grid: { left: 48, right: 12, top: 24, bottom: 24 },
      tooltip: {
        trigger: 'axis',
        formatter: (params: unknown) => {
          const items = params as Array<{ dataIndex: number; seriesName: string; value: number | null; marker: string }>;
          if (!items.length) return '';
          const day = line.daily[items[0].dataIndex];
          const note = day?.launch ? ' · arranque' : day?.paused_at ? ` · pausa ${day.paused_at}` : '';
          const rows = items.map((item) => tooltipRow(item.seriesName, formatMeasure(spec.measure, item.value), item.marker));
          const fill = day?.fill !== null && day?.fill !== undefined ? tooltipRow('% del tope', pct(day.fill, 0)) : '';
          return tooltipTitle(`${dayMonth(day?.date)}${note}`) + rows.join('') + fill;
        },
      },
      xAxis: { type: 'category', data: line.daily.map((day) => dayMonth(day.date)) },
      yAxis: {
        type: 'value',
        max: axisMaxForCap(line, spec),
        axisLabel: { formatter: (value: number) => num(value) },
      },
      series: [
        {
          name: spec.measure === 'cost' ? 'Gasto' : 'Impresiones',
          type: 'bar',
          barMaxWidth: 20,
          data: line.daily.map((day) => {
            const value = spec.measure === 'cost' ? day.cost : day.units;
            const muted = day.launch || !!day.paused_at;
            return { value, itemStyle: { color: muted ? MUTED_BAR : barColor } };
          }),
          ...(spec.capReference !== null
            ? {
                markLine: {
                  symbol: 'none',
                  silent: true,
                  lineStyle: { color: capColor, type: 'dashed', width: 1.5 },
                  label: {
                    formatter: `tope ${num(spec.capReference)}`,
                    color: ADVIA_CHART_TOKENS.textSecondary,
                    fontSize: 11,
                    position: 'insideEndTop',
                  },
                  data: [{ yAxis: spec.capReference }],
                },
              }
            : {}),
        },
        ...(spec.capSeries
          ? [
              {
                name: 'Tope',
                type: 'line' as const,
                step: 'middle' as const,
                smooth: false,
                symbol: 'none',
                lineStyle: { color: capColor, width: 1.5 },
                itemStyle: { color: capColor },
                data: spec.capSeries,
              },
            ]
          : []),
      ],
    };
  }, [line, spec]);

  const subtitle = [
    `Certeza ${line.cert.level.toLowerCase()}`,
    line.at_cap !== null ? `${pct(line.at_cap, 0)} días a tope` : null,
    spec.measure === 'units' ? 'impresiones vs tope actual' : spec.capSeries ? 'gasto vs tope (€)' : 'sin tope',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="adv-card adv-card--kpi px-4 pt-3 pb-1 min-w-0">
      <p className="font-bold text-adv-body-s text-adv-primary truncate" title={line.name}>
        {line.name}
      </p>
      <p className="text-adv-body-s text-adv-tertiary truncate" title={subtitle}>
        {subtitle} · total <span className="adv-num">{formatMeasure(spec.measure, sumOf(line, spec.measure))}</span>
      </p>
      <ReactECharts
        option={option}
        style={{ height: CHART_HEIGHT }}
        notMerge
        theme={ADVIA_ECHARTS_THEME}
        opts={ADVIA_CHART_OPTS}
      />
    </div>
  );
}

function sumOf(line: ReportLine, measure: Measure): number {
  return line.daily.reduce((total, day) => total + ((measure === 'cost' ? day.cost : day.units) ?? 0), 0);
}
